import type { JSX } from 'preact';
import { registry } from '../../sim/registry';
import type { ActionKey } from '../../sim/types';
import { skillIcon } from '../../ui/icons';
import { openPanel } from '../../ui/app/store';
import type { HudMarker, HudSkill } from './runtime';
import { type SeaHudView, sailGame, seaHud } from './system';

/**
 * The helm HUD shown while sailing (the combat cluster is hidden at sea): a
 * compass strip with ports, weather and wreckage, hazard warnings, the hull
 * gauge, speed, the ship's three abilities and the tug notice.
 */

const SPAN = 90;
const CARDINALS: Record<number, string> = { 0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW' };

/** Signed difference to → from in degrees, in [-180, 180). */
const rel = (from: number, to: number): number => ((((to - from + 180) % 360) + 360) % 360) - 180;
const cardinal = (deg: number): string => CARDINALS[(Math.round(deg / 45) * 45) % 360] ?? 'N';
/** Ticks fade toward the ends of the strip. */
const fade = (r: number): number => Math.max(0, Math.min(1, (1 - Math.abs(r) / SPAN) * 4));
const metres = (d: number): string => (d >= 1000 ? `${(d / 1000).toFixed(1)} km` : `${Math.round(d)} m`);

function Glyph({ kind }: { kind: HudMarker['kind'] | 'warn' }) {
  const common = { fill: 'none', stroke: 'currentColor', 'stroke-width': 2, 'stroke-linecap': 'round' as const, 'stroke-linejoin': 'round' as const };
  switch (kind) {
    case 'port':
      return (
        <svg viewBox="0 0 24 24" class="sh-glyph">
          <circle cx="12" cy="5" r="2.2" {...common} />
          <path d="M12 7.5 V20 M7 11 H17 M4.5 14 C5.5 18.5 9 20.5 12 20.5 C15 20.5 18.5 18.5 19.5 14" {...common} />
        </svg>
      );
    case 'storm':
      return (
        <svg viewBox="0 0 24 24" class="sh-glyph">
          <path d="M13.5 2.5 L6 13.5 H11.5 L10 21.5 L18 10 H12.5 Z" {...common} fill="currentColor" fill-opacity="0.35" />
        </svg>
      );
    case 'fog':
      return (
        <svg viewBox="0 0 24 24" class="sh-glyph">
          <path d="M3 8 C6 6 9 10 12 8 S18 6 21 8 M3 13 C6 11 9 15 12 13 S18 11 21 13 M3 18 C6 16 9 20 12 18 S18 16 21 18" {...common} />
        </svg>
      );
    case 'wreck':
      return (
        <svg viewBox="0 0 24 24" class="sh-glyph">
          <path d="M4 10 H20 V19 H4 Z M4 10 L6 5.5 H18 L20 10 M10 13.5 H14" {...common} fill="currentColor" fill-opacity="0.25" />
        </svg>
      );
    case 'warn':
      return (
        <svg viewBox="0 0 24 24" class="sh-glyph">
          <path d="M12 3 L22 20 H2 Z M12 9.5 V14 M12 17 V17.2" {...common} />
        </svg>
      );
  }
}

function Compass({ heading, markers }: { heading: number; markers: HudMarker[] }) {
  const ticks: JSX.Element[] = [];
  for (let d = 0; d < 360; d += 15) {
    const r = rel(heading, d);
    if (Math.abs(r) > SPAN) continue;
    const card = CARDINALS[d];
    ticks.push(
      <i key={d} class={`sh-tick${card ? ' is-card' : ''}${d % 90 === 0 ? ' is-main' : ''}`} style={{ left: `${50 + (r / SPAN) * 50}%`, opacity: fade(r) }}>
        {card ? <b>{card}</b> : d % 30 === 0 ? <span>{d}</span> : null}
      </i>,
    );
  }
  return (
    <div class="sh-compass">
      <div class="sh-compass__strip">
        {ticks}
        {markers.map((m, i) => {
          const r = rel(heading, m.bearing);
          const edge = Math.abs(r) > SPAN;
          const x = 50 + (Math.max(-SPAN, Math.min(SPAN, r)) / SPAN) * 50;
          return (
            <div key={`${m.kind}${i}`} class={`sh-mark is-${m.kind}${edge ? ' is-edge' : ''}`} style={{ left: `${x}%`, opacity: edge ? 0.5 : Math.max(0.55, fade(r)) }} title={`${m.label} · ${metres(m.distance)}`}>
              <Glyph kind={m.kind} />
              <span>{m.distance < 1 ? 'here' : metres(m.distance)}</span>
            </div>
          );
        })}
      </div>
      <div class="sh-compass__caret" />
      <div class="sh-compass__hdg">
        <b>{Math.round(heading) % 360}°</b> {cardinal(heading)}
      </div>
    </div>
  );
}

function Alerts({ s }: { s: SeaHudView }) {
  if (s.towing) return null;
  const w = s.warning;
  let alert: JSX.Element | null = null;
  if (w?.inside && w.hazard.kind === 'storm')
    alert = (
      <div class="sh-alert is-storm">
        <Glyph kind="storm" />
        Storm — the hull is taking a beating{s.hullRate > 0.05 ? ` (−${s.hullRate.toFixed(1)}/s)` : ''}
      </div>
    );
  else if (w?.inside)
    alert = (
      <div class="sh-alert is-fog">
        <Glyph kind="fog" />
        Fog bank — keep a sharp lookout
      </div>
    );
  else if (w && w.ahead && w.distance < 70)
    alert = (
      <div class={`sh-alert is-ahead is-${w.hazard.kind}`}>
        <Glyph kind="warn" />
        {w.hazard.kind === 'storm' ? 'Storm' : 'Fog bank'} ahead · {metres(w.distance)}
      </div>
    );
  return (
    <div class="sh-alerts">
      {alert}
      {s.salvageHint && (
        <div class="sh-alert is-wreck">
          <Glyph kind="wreck" />
          {s.salvageHint.name} · {metres(s.salvageHint.distance)} — sail alongside, or blast it open with the cannon <kbd>W</kbd>
        </div>
      )}
    </div>
  );
}

function press(key: string): void {
  const g = sailGame.current;
  const p = g?.player;
  const w = g?.zone?.world;
  if (g && p && w) w.pressKey(p, key as ActionKey, { ...g.cursorPoint });
}

function Slot({ sk }: { sk: HudSkill }) {
  const def = registry.skills.get(sk.id);
  const icon = def ? skillIcon(def) : '';
  const frac = sk.cdMax > 0 ? Math.min(1, sk.cd / sk.cdMax) : 0;
  return (
    <button
      type="button"
      class={`sh-slot interactive${sk.active ? ' is-active' : ''}${sk.cd > 0 ? ' is-cd' : ''}`}
      title={def ? `${def.name} — ${def.description}` : sk.name}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={() => press(sk.key)}
    >
      {icon && <img src={icon} alt="" draggable={false} />}
      <i class="sh-slot__cd" style={`--cd:${frac.toFixed(3)}turn`} />
      {sk.cd > 0 && <span class="sh-slot__n">{sk.cd >= 10 ? Math.ceil(sk.cd) : sk.cd.toFixed(1)}</span>}
      <kbd>{sk.key}</kbd>
    </button>
  );
}

function Rose({ heading }: { heading: number }) {
  return (
    <svg viewBox="-20 -20 40 40" class="sh-rose" aria-hidden="true">
      <circle r="17.5" class="sh-rose__ring" />
      {[0, 90, 180, 270].map((d) => (
        <line key={d} x1="0" y1="-17.5" x2="0" y2="-13.5" transform={`rotate(${d})`} class="sh-rose__tick" />
      ))}
      <text y="-7.5" class="sh-rose__n">
        N
      </text>
      <g transform={`rotate(${heading})`}>
        <path d="M0 -15 L3.6 1 L0 -1.5 L-3.6 1 Z" class="sh-rose__needle" />
        <path d="M0 15 L2.6 2 L0 3.2 L-2.6 2 Z" class="sh-rose__tail" />
      </g>
    </svg>
  );
}

function Helm({ s }: { s: SeaHudView }) {
  const pct = s.maxHull > 0 ? Math.max(0, Math.min(1, s.hull / s.maxHull)) : 0;
  const tone = pct < 0.15 ? 'crit' : pct < 0.35 ? 'low' : 'ok';
  return (
    <div class={`sh-helm is-${tone}`}>
      <div class="sh-hullrow">
        <span class="sh-label">Hull</span>
        <div class={`sh-hull${s.patching ? ' is-patching' : ''}`}>
          <i style={{ transform: `scaleX(${pct.toFixed(4)})` }} />
          <span>
            {Math.ceil(s.hull)} / {s.maxHull}
          </span>
        </div>
        <span class={`sh-rate${s.hullRate > 0.15 ? ' is-on' : ''}`}>−{s.hullRate.toFixed(1)}/s</span>
      </div>
      <div class="sh-row">
        <div class="sh-speed">
          <div class="sh-speed__num">
            <b>{s.knots.toFixed(1)}</b>
            <small>kn</small>
          </div>
          <div class={`sh-throttle${s.fullSail > 0 ? ' is-boost' : ''}`}>
            <i style={{ transform: `scaleX(${Math.min(1, s.knots / Math.max(1, s.maxKnots * 1.5)).toFixed(3)})` }} />
            <em style={{ left: `${(100 / 1.5).toFixed(1)}%` }} />
          </div>
          <div class="sh-speed__sub">{s.fullSail > 0 ? `Full sail · ${Math.ceil(s.fullSail)}s` : `${s.shipName} · Lv ${s.level}`}</div>
        </div>
        <div class="sh-skills">
          {s.skills.map((sk) => (
            <Slot key={sk.id} sk={sk} />
          ))}
        </div>
        <div class="sh-nav">
          <Rose heading={s.heading} />
          <button type="button" class="btn sm sh-shipbtn interactive" onPointerDown={(e) => e.stopPropagation()} onClick={() => openPanel('sail')}>
            Ship
          </button>
        </div>
      </div>
      {s.voyage < 30 && !s.towing && (
        <div class="sh-hint">
          Hold the mouse on the water to steer · <kbd>Q</kbd> full sail · <kbd>W</kbd> cannons · <kbd>E</kbd> patch · sail into a pier to dock
        </div>
      )}
    </div>
  );
}

function Tow({ s }: { s: SeaHudView }) {
  if (!s.towing) return null;
  return (
    <div class="sh-tow">
      <div class="sh-tow__card">
        <div class="sh-tow__t">{s.towing.free ? 'Not seaworthy' : 'Hull breached'}</div>
        <div class="sh-tow__d">A harbour tug is towing {s.shipName} to {s.towing.port}…</div>
        <div class="sh-tow__bar">
          <i />
        </div>
      </div>
    </div>
  );
}

export function SeaHud() {
  const s = seaHud.value;
  if (!s) return null;
  return (
    <div class="sea-hud">
      <Compass heading={s.heading} markers={s.markers} />
      <Alerts s={s} />
      <Helm s={s} />
      <Tow s={s} />
    </div>
  );
}
